import { useState } from "react";
import { Bot, Coffee, MessageCircle, Star, QrCode, ChevronDown } from "lucide-react";
import GuestAppContent from "../components/GuestAppContent";
import { useGetStrutture } from "../lib/supabaseQueries";
import type { GuestAppConfig } from "../lib/guestAppStore";

function strutturaToConfig(s: any): GuestAppConfig {
  return {
    id: s.id,
    nome: s.nome ?? "Struttura",
    location: [s.citta, s.cap].filter(Boolean).join(", ") || s.indirizzo || "",
    emoji: "🏠",
    descrizione: s.descrizione ?? "",
    wifi: s.wifi ?? "",
    regole: s.regole ?? [],
    dotazioni: s.dotazioni ?? [],
    servizi: s.servizi ?? [],
    host: {
      nome: s.host_nome ?? "",
      initials: (s.host_nome ?? "H")[0].toUpperCase(),
      ruolo: s.host_ruolo ?? "Host",
      risposta: s.host_risposta ?? "",
      bio: s.host_bio ?? "",
      telefono: s.host_telefono ?? "",
      whatsapp: s.host_whatsapp ?? "",
      rating: s.host_rating ?? 5,
    },
    chatbotInfo: s.chatbot_info ?? "",
    knowledgeBase: s.knowledge_base ?? "",
  };
}

export default function GuestAppAnteprima() {
  const { data: strutture = [], isLoading } = useGetStrutture();
  const [strutturaId, setStrutturaId] = useState<string>("");

  const selectedId = strutturaId ? Number(strutturaId) : (strutture[0]?.id ?? 0);
  const strutturaRaw = strutture.find(s => s.id === selectedId) ?? strutture[0];
  const struttura = strutturaRaw ? strutturaToConfig(strutturaRaw) : null;
  const FEATURES = [
    { Icon: Bot,         color: "bg-primary/10 text-primary",      titolo: "Chatbot AI",       desc: "Risponde alle domande degli ospiti cercando in struttura, servizi e host" },
    { Icon: Coffee,      color: "bg-amber-50 text-amber-600",       titolo: "Servizi",          desc: "Grid cliccabile con popup per ogni servizio disponibile" },
    { Icon: MessageCircle, color: "bg-blue-50 text-blue-600",       titolo: "Contatta l'host",  desc: "WhatsApp e telefono direttamente dalla sezione Host" },
    { Icon: Star,        color: "bg-yellow-50 text-yellow-600",     titolo: "Recensioni",       desc: "L'ospite lascia recensione interna e viene rimandato su Google" },
    { Icon: QrCode,      color: "bg-emerald-50 text-emerald-600",   titolo: "Accesso QR",       desc: "Link o QR code inviato automaticamente prima del check-in" },
  ];

  if (isLoading) return <div className="py-20 text-center text-muted-foreground text-sm">Caricamento strutture...</div>;

  if (!struttura) return (
    <div className="py-20 text-center">
      <p className="text-muted-foreground font-medium mb-1">Nessuna struttura trovata</p>
      <p className="text-sm text-muted-foreground/70">Aggiungi prima una struttura dalla sezione Strutture.</p>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Anteprima Guest App</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Così vedrà l'app il tuo ospite</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">

        {/* Phone mockup */}
        <div className="flex-shrink-0 mx-auto lg:mx-0 space-y-3">
          {/* Struttura selector */}
          <div className="relative w-[300px]">
            <select
              value={selectedId}
              onChange={e => setStrutturaId(e.target.value)}
              className="w-full appearance-none bg-card border border-border rounded-xl pl-4 pr-9 py-2.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer">
              {strutture.map(s => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>

          {/* Phone frame */}
          <div className="relative w-[300px] mx-auto">
            {/* Outer shell */}
            <div className="bg-[#1c1c1e] rounded-[44px] p-[10px] shadow-2xl ring-1 ring-white/10">
              {/* Screen */}
              <div className="bg-card rounded-[36px] overflow-hidden flex flex-col" style={{ height: 596 }}>
                {/* Notch bar */}
                <div className="bg-foreground flex items-center justify-between px-5 pt-2.5 pb-2 flex-shrink-0 relative">
                  <span className="text-white text-[10px] font-semibold">9:41</span>
                  {/* Dynamic island / notch */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-2 w-20 h-5 bg-black rounded-full" />
                  <div className="flex items-center gap-1">
                    <svg width="12" height="9" viewBox="0 0 12 9" fill="none"><rect x="0" y="3" width="2" height="6" rx="0.5" fill="white" fillOpacity=".4"/><rect x="3" y="2" width="2" height="7" rx="0.5" fill="white" fillOpacity=".6"/><rect x="6" y="1" width="2" height="8" rx="0.5" fill="white" fillOpacity=".8"/><rect x="9" y="0" width="2" height="9" rx="0.5" fill="white"/></svg>
                    <svg width="14" height="11" viewBox="0 0 14 11" fill="none"><path d="M7 2.5C9.2 2.5 11.2 3.4 12.6 4.9L14 3.4C12.2 1.3 9.7 0 7 0C4.3 0 1.8 1.3 0 3.4L1.4 4.9C2.8 3.4 4.8 2.5 7 2.5Z" fill="white" fillOpacity=".5"/><path d="M7 5.5C8.4 5.5 9.6 6.1 10.5 7L11.9 5.5C10.6 4.2 8.9 3.5 7 3.5C5.1 3.5 3.4 4.2 2.1 5.5L3.5 7C4.4 6.1 5.6 5.5 7 5.5Z" fill="white" fillOpacity=".75"/><circle cx="7" cy="9.5" r="1.5" fill="white"/></svg>
                    <svg width="22" height="11" viewBox="0 0 22 11" fill="none"><rect x="0.5" y="0.5" width="18" height="10" rx="2.5" stroke="white" strokeOpacity=".35"/><rect x="2" y="2" width="13" height="7" rx="1.5" fill="white"/><path d="M20 3.5V7.5C20.8 7.2 21.5 6.5 21.5 5.5C21.5 4.5 20.8 3.8 20 3.5Z" fill="white" fillOpacity=".4"/></svg>
                  </div>
                </div>
                <GuestAppContent struttura={struttura} compact={true} />
              </div>
            </div>
            {/* Side buttons */}
            <div className="absolute -right-[3px] top-24 w-[3px] h-12 bg-[#3a3a3c] rounded-r-sm" />
            <div className="absolute -left-[3px] top-20 w-[3px] h-8 bg-[#3a3a3c] rounded-l-sm" />
            <div className="absolute -left-[3px] top-32 w-[3px] h-12 bg-[#3a3a3c] rounded-l-sm" />
          </div>

          {/* Active badge */}
          <div className="flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-muted-foreground font-medium">{struttura.nome} · Attiva</span>
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 space-y-4 min-w-0">

          {/* Struttura info card */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="mb-4">
              <h2 className="font-bold text-foreground">{struttura.nome}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{struttura.location}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "Servizi",   value: struttura.servizi.length,   color: "bg-amber-50 text-amber-700" },
                { label: "Dotazioni", value: struttura.dotazioni.length,  color: "bg-blue-50 text-blue-700" },
                { label: "Regole",    value: struttura.regole.length,     color: "bg-muted/60 text-foreground" },
              ].map(s => (
                <div key={s.label} className={`${s.color} rounded-xl p-3 text-center`}>
                  <div className="text-xl font-bold">{s.value}</div>
                  <div className="text-[10px] font-medium mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="font-semibold text-foreground mb-3 text-sm">Funzionalità incluse</h2>
            <div className="space-y-3">
              {FEATURES.map(f => (
                <div key={f.titolo} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${f.color}`}>
                    <f.Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{f.titolo}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
